import { BaseApiClient } from '../client'
import type {
  Member,
  MemberWithWallet,
  CreateMemberInput,
  UpdateMemberInput,
  CleanupResult,
  CleanupJobStatus,
} from '../types'

export class MembersApi extends BaseApiClient {
  async list(
    page = 1,
    limit = 20,
    search?: string,
    status?: string,
    sortBy?: string,
    sortDirection?: string
  ) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })
    if (search) params.append('q', search)
    if (status && status !== 'all') params.append('status', status)
    if (sortBy) params.append('sortBy', sortBy)
    if (sortDirection) params.append('sortDirection', sortDirection)

    return this.requestPaginated<MemberWithWallet>(`/api/admin/members?${params.toString()}`)
  }

  async get(id: string) {
    return this.request<MemberWithWallet>(`/api/admin/members/${id}`)
  }

  async create(data: CreateMemberInput) {
    return this.request<Member>('/api/admin/members', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async update(id: string, data: UpdateMemberInput) {
    return this.request<Member>(`/api/admin/members/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async sync(id: string) {
    return this.request<Member>(`/api/admin/members/${id}/sync`, {
      method: 'POST',
    })
  }

  async cancel(id: string) {
    return this.request<{ data: Member; warnings: string[] }>(`/api/admin/members/${id}/cancel`, {
      method: 'POST',
    })
  }

  async cleanupDuplicates(execute = false) {
    return this.request<CleanupResult>('/api/admin/cleanup', {
      method: 'POST',
      body: JSON.stringify({ dryRun: !execute }),
    })
  }

  async getCleanupStatus() {
    return this.request<CleanupJobStatus | null>('/api/admin/cleanup/status')
  }

  async startCleanup(dryRun: boolean) {
    return this.request<CleanupJobStatus>('/api/admin/cleanup', {
      method: 'POST',
      body: JSON.stringify({ dryRun }),
    })
  }

  async cancelCleanup(jobId: string) {
    return this.request<{ success: boolean; message: string }>(`/api/admin/cleanup/${jobId}`, {
      method: 'DELETE',
    })
  }

  async startResyncKlaviyo() {
    return this.request<CleanupJobStatus>('/api/admin/cleanup/resync-klaviyo', {
      method: 'POST',
    })
  }

  async getResyncStatus() {
    return this.request<CleanupJobStatus | null>('/api/admin/cleanup/resync-klaviyo/status')
  }
}
